/**
 * Denaro — sempre interi in centesimi, mai float.
 *
 * L'invariante vale anche in ingresso: i decimali che arrivano da XML, CSV o
 * form vengono convertiti leggendo le cifre, non passando da `Number`.
 * `parseFloat("0.29") * 100` fa 28.999999999999996 e arrotondato torna giusto,
 * ma su `1.005` no: è il tipo di errore che in tesoreria si scopre tardi.
 */
const DECIMALE = /^\s*([+-]?)(\d{1,15})(?:[.,](\d+))?\s*$/;
/**
 * Converte una stringa decimale in centesimi senza passare per il floating
 * point. Accetta sia il punto sia la virgola come separatore decimale.
 * Le cifre oltre la seconda sono arrotondate half-up sul valore assoluto.
 */
export function centesimiDaDecimale(valore) {
    if (typeof valore === 'number') {
        if (!Number.isFinite(valore))
            throw new Error(`Importo non finito: ${valore}`);
        // Passa dalla rappresentazione testuale: preserva le cifre che l'utente
        // (o il file) ha scritto davvero, senza l'errore di rappresentazione.
        return centesimiDaDecimale(valore.toFixed(6));
    }
    const m = DECIMALE.exec(valore);
    if (!m)
        throw new Error(`Importo non riconosciuto: "${valore}"`);
    const [, segno, intero, decimali = ''] = m;
    const cent = decimali.padEnd(3, '0').slice(0, 3);
    const unita = BigInt(intero) * 100n + BigInt(cent.slice(0, 2));
    const terzaCifra = Number(cent[2]);
    const arrotondato = terzaCifra >= 5 ? unita + 1n : unita;
    const risultato = segno === '-' ? -arrotondato : arrotondato;
    if (risultato > BigInt(Number.MAX_SAFE_INTEGER) || risultato < BigInt(-Number.MAX_SAFE_INTEGER)) {
        throw new Error(`Importo fuori scala: "${valore}"`);
    }
    return Number(risultato);
}
/** Centesimi → stringa decimale con il punto, per XML e API. */
export function decimaleDaCentesimi(cent) {
    const negativo = cent < 0;
    const abs = Math.abs(Math.round(cent));
    const s = String(abs).padStart(3, '0');
    return `${negativo ? '-' : ''}${s.slice(0, -2)}.${s.slice(-2)}`;
}
/**
 * `useGrouping: 'always'` non è un vezzo: per l'italiano CLDR dichiara
 * `minimumGroupingDigits=2`, quindi di suo Intl scrive `1234,56 €` e mette il
 * punto solo da cinque cifre in su. In una colonna di importi incolonnati
 * quella disparità si legge come un errore di battitura, e la prassi contabile
 * italiana il separatore lo mette sempre.
 */
const FMT_EUR = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: 'always',
});
const FMT_EUR_COMPATTO = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: 'always',
});
/** `123456` → `"1.234,56 €"` */
export function formattaEuro(cent, opzioni) {
    const fmt = opzioni?.compatto ? FMT_EUR_COMPATTO : FMT_EUR;
    return fmt.format(Math.round(cent) / 100);
}
/** Somma di centesimi, senza sorprese su array vuoti. */
export function somma(valori) {
    let t = 0;
    for (const v of valori)
        t += v;
    return t;
}
/**
 * IVA su un imponibile in centesimi. Aliquota in percentuale intera o decimale
 * (22, 10, 4, 5). Arrotondamento half-up, come da prassi fiscale italiana.
 */
export function ivaSuImponibile(imponibileCent, aliquota) {
    const prodotto = (imponibileCent * aliquota) / 100;
    return Math.sign(prodotto) * Math.round(Math.abs(prodotto));
}
/** Ripartisce un importo in n quote uguali, distribuendo il resto sui primi. */
export function ripartisci(totaleCent, quote) {
    if (quote <= 0)
        throw new Error('Numero di quote non valido');
    const segno = Math.sign(totaleCent) || 1;
    const abs = Math.abs(totaleCent);
    const base = Math.floor(abs / quote);
    const resto = abs - base * quote;
    return Array.from({ length: quote }, (_, i) => segno * (base + (i < resto ? 1 : 0)));
}
//# sourceMappingURL=denaro.js.map